import { useCallback, useEffect, useState } from "react";
import { reportClientError } from "../../../frontend/lib/error-reporter.ts";
import { getProjectById } from "../../leaderboard/api/leaderboard-api.ts";
import type { Project } from "../../leaderboard/types/index.ts";
import {
  approveAllProjects,
  approveProjects,
  getReviewPending,
  getReviewStats,
  rejectProjects,
} from "../api/discovery-api.ts";
import { useDiscoveryStatusQuery } from "../hooks/use-discovery-status-query.ts";
import type {
  DiscoveryRunSummary,
  DiscoverySourceStatus,
  ReviewPendingItem,
  ReviewStatsResponse,
} from "../types/index.ts";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatRelative(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function formatEta(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes <= 1) return "<1 min";
  return `~${minutes.toFixed(1)} min`;
}

function formatQueueStatus(
  status: "queued" | "scoring" | "scored" | "failed" | "deferred"
): string {
  switch (status) {
    case "queued":
      return "Queued";
    case "scoring":
      return "Scoring";
    case "scored":
      return "Scored";
    case "failed":
      return "Failed";
    case "deferred":
      return "Deferred";
    default:
      return status;
  }
}

function formatAuditPolicy(policy: "enqueue_immediately" | "discovery_only"): string {
  return policy === "enqueue_immediately" ? "Enqueue Audit" : "Discovery Only";
}

const SOURCE_LABELS: Record<string, string> = {
  defillama: "DeFiLlama",
  moni_mindshare: "Moni",
  rootdata: "RootData",
  exa: "Exa",
};

interface DiscoveryViewProps {
  onOpenProjectDetail: (project: Project) => void;
}

function SourceRow({ id, status }: { id: string; status: DiscoverySourceStatus }) {
  const label = SOURCE_LABELS[id] ?? id;
  return (
    <div className="discovery-source-row">
      <span className={`discovery-source-row__dot ${status.enabled ? "configured" : "missing"}`} />
      <span className="discovery-source-row__name">{label}</span>
      {status.enabled ? (
        <>
          {status.candidatesFound != null && (
            <span className="discovery-source-row__stat">{status.candidatesFound} found</span>
          )}
          {status.lastRunAt && (
            <span className="discovery-source-row__time">{formatRelative(status.lastRunAt)}</span>
          )}
        </>
      ) : (
        <span className="discovery-source-row__stat discovery-source-row__stat--off">disabled</span>
      )}
    </div>
  );
}

function LastRunSummary({ run }: { run: DiscoveryRunSummary }) {
  return (
    <div className="discovery-lastrun">
      <div className="discovery-lastrun__header">
        <span className="discovery-panel__title">Last Run</span>
        <span
          className={`discovery-status-pill ${run.status === "completed" ? "discovery-status-pill--running" : "discovery-status-pill--idle"}`}
        >
          {run.status}
        </span>
      </div>
      <div className="discovery-lastrun__grid">
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val">{run.candidatesTotal}</span>
          <span className="discovery-lastrun__key">Candidates</span>
        </div>
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val discovery-lastrun__val--positive">
            {run.discovered}
          </span>
          <span className="discovery-lastrun__key">Discovered</span>
        </div>
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val">{run.enqueued}</span>
          <span className="discovery-lastrun__key">Enqueued</span>
        </div>
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val">{run.auditSkippedByPolicy}</span>
          <span className="discovery-lastrun__key">Deferred</span>
        </div>
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val">{run.skippedHasToken}</span>
          <span className="discovery-lastrun__key">Has Token</span>
        </div>
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val">{run.skippedExisting}</span>
          <span className="discovery-lastrun__key">Existing</span>
        </div>
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val">{run.skippedNoHandle}</span>
          <span className="discovery-lastrun__key">No Handle</span>
        </div>
        <div className="discovery-lastrun__item">
          <span
            className={`discovery-lastrun__val ${run.errors > 0 ? "discovery-lastrun__val--negative" : ""}`}
          >
            {run.errors}
          </span>
          <span className="discovery-lastrun__key">Errors</span>
        </div>
        <div className="discovery-lastrun__item">
          <span className="discovery-lastrun__val">
            {run.durationMs != null ? `${(run.durationMs / 60_000).toFixed(1)}m` : "—"}
          </span>
          <span className="discovery-lastrun__key">Duration</span>
        </div>
      </div>
      <div className="discovery-meta-row">
        <span>Policy: {formatAuditPolicy(run.auditEnqueuePolicy)}</span>
        <span>Started: {formatDateTime(run.startedAt)}</span>
        <span>Finished: {formatDateTime(run.finishedAt)}</span>
      </div>
    </div>
  );
}

/**
 * Render a source-aware one-liner explaining why this candidate is in
 * the queue. Reads fields already on the project row — no additional
 * API calls. Returns null when we can't build anything meaningful;
 * the UI just skips the context line in that case.
 */
function buildDiscoveryContext(item: ReviewPendingItem): string | null {
  const source = item.discovery_source ?? "";
  const rounds = Array.isArray(item.funding_rounds) ? item.funding_rounds : [];
  // Pick the newest round by date rather than `rounds[length-1]`.
  // DefiLlama's discovery pipeline sorts rounds DESC (newest first), so
  // the last element is the OLDEST — same bug class as the prefetched
  // fallback in raises-combined.ts. Re-audit finding #4 (2026-04-22).
  //
  // `ReviewPendingRound.date` is typed `string | null` (ISO / YYYY-MM),
  // but legacy rows from older JSONB writes carry numeric unix seconds.
  // Coerce both to unix seconds before comparing; Codex P2 on the
  // initial fix flagged that a numbers-only check fell back to 0 and
  // thus still selected the last array element.
  const toUnixSeconds = (d: unknown): number => {
    if (typeof d === "number") return d > 0 ? d : 0;
    if (typeof d === "string" && d.length > 0) {
      const ms = new Date(d).getTime();
      if (Number.isFinite(ms) && ms > 0) return Math.floor(ms / 1000);
    }
    return 0;
  };
  let latestRound: (typeof rounds)[number] | undefined;
  let latestDate = 0;
  for (const r of rounds) {
    const d = toUnixSeconds(r?.date);
    if (d >= latestDate) {
      latestDate = d;
      latestRound = r;
    }
  }
  if (!latestRound) latestRound = rounds[0];

  if (source === "rootdata") {
    // RootData candidates land here via get_fac (fundraising rounds).
    // Surface the latest round + date so the reviewer can see why
    // RootData surfaced it.
    if (latestRound?.round) {
      const date = formatRoundDate(latestRound.date);
      const amount = formatRoundAmount(latestRound.amount);
      const parts = [`${latestRound.round} round`];
      if (amount) parts.push(amount);
      if (date) parts.push(date);
      return `RootData — ${parts.join(" · ")}`;
    }
    return "RootData — fundraising round detected";
  }

  if (source === "moni_mindshare") {
    // Moni surfaces smart-money mindshare winners. We don't persist
    // the per-candidate smartsCount / mindshareChange to the project
    // row yet (Phase 2), so we just flag the source.
    return "Moni — smart-money mindshare winner (category-top in the last 7 days)";
  }

  if (source === "defillama") {
    const pieces: string[] = [];
    if (item.defillama_category) pieces.push(item.defillama_category);
    if (latestRound?.round) {
      const date = formatRoundDate(latestRound.date);
      const amount = formatRoundAmount(latestRound.amount);
      const round = [latestRound.round, amount, date].filter(Boolean).join(" · ");
      pieces.push(round);
    }
    return pieces.length > 0 ? `DefiLlama — ${pieces.join(" · ")}` : "DefiLlama — raise detected";
  }

  if (source === "exa") {
    return "Exa Search — surfaced by neural search on recent crypto raises";
  }

  if (source === "airtable") {
    return "Imported from Airtable (legacy list)";
  }

  return null;
}

/** Milestone compact dollars: 5.4M, 12.0M, 1.2B, etc. */
function formatRoundAmount(amount: number | null | undefined): string | null {
  if (!amount || amount <= 0) return null;
  if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
  if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
  if (amount >= 1e3) return `$${(amount / 1e3).toFixed(0)}K`;
  return `$${amount.toFixed(0)}`;
}

function formatRoundDate(raw: string | null | undefined): string | null {
  if (typeof raw !== "string" || raw.length < 4) return null;
  // Accept ISO dates, YYYY-MM, YYYY. Trim to YYYY-MM for display.
  const match = raw.match(/^(\d{4})(?:-(\d{2}))?/);
  if (!match) return null;
  const [, year, month] = match;
  if (!month) return year ?? null;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const idx = Number(month) - 1;
  const mName = idx >= 0 && idx < 12 ? monthNames[idx] : null;
  return mName ? `${mName} ${year}` : (year ?? null);
}

/**
 * Pick up to ~6 most useful tags for the inline tag strip. Prefers
 * RootData tags (most specific), then DefiLlama category/sector, then
 * chains. Deduped case-insensitively.
 */
function pickRelevantTags(item: ReviewPendingItem): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (v: string | null | undefined) => {
    if (!v) return;
    const trimmed = v.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(trimmed);
  };
  (item.rootdata_tags ?? []).forEach(add);
  add(item.defillama_category);
  add(item.defillama_sector);
  (item.rootdata_ecosystem ?? []).forEach(add);
  (item.defillama_chains ?? []).forEach(add);
  return out;
}

// Phase 2 of review-queue-v2: each reason triggers a specific auto-filter
// downstream (blocklist, established-entity insert, memecoin keyword
// suggestion). The `effect` string surfaces that in the reject modal so
// the operator sees the consequence before clicking.
//
// The slug set must stay in sync with REJECTION_REASONS in
// src/services/discovery/rejection-filters.ts (backend Zod enum). Labels
// and effect copy live here because they're UI concerns.
const REJECTION_REASONS: { value: string; label: string; effect: string }[] = [
  {
    value: "has_token",
    label: "Already has token",
    effect: "Blocklists handle + name variants.",
  },
  {
    value: "memecoin",
    label: "Memecoin",
    effect: "Blocklists handle + queues name tokens for operator-approved keyword filtering.",
  },
  {
    value: "never_tokenizing",
    label: "Exchange / tradfi / won't tokenize",
    effect: "Blocklists handle AND adds project to never-tokenizing entity list.",
  },
  { value: "not_crypto", label: "Not crypto", effect: "Blocklists handle + name variants." },
  {
    value: "dead_project",
    label: "Dead / dormant",
    effect: "Blocklists handle for 6 months (may revive).",
  },
  {
    value: "duplicate",
    label: "Duplicate of existing",
    effect: "No auto-filter. Manual merge pending.",
  },
  { value: "spam", label: "Spam / low-effort", effect: "Blocklists handle + name variants." },
  { value: "other", label: "Other (requires note)", effect: "Note-only. No auto-filter." },
];

// One row's per-project state inside the reject modal. `name` / `handle`
// are snapshots from the list item so the modal renders even if the
// underlying item leaves `items` during the action.
interface RejectRow {
  projectId: string;
  name: string | null;
  handle: string;
  reason: string;
  note: string;
}

function ReviewQueue({ onOpenProject }: { onOpenProject: (id: string) => void }) {
  const [items, setItems] = useState<ReviewPendingItem[]>([]);
  const [stats, setStats] = useState<ReviewStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRows, setRejectRows] = useState<RejectRow[]>([]);
  // "Apply to all" dropdown is a one-shot action — select a reason, it
  // bulk-sets all rows, then the dropdown resets to the placeholder.
  // Tracked in state so React can control it cleanly; setting to "" from
  // the onChange handler works without fighting React's controlled
  // component contract.
  const [applyAllReason, setApplyAllReason] = useState<string>("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, st] = await Promise.all([getReviewPending({ limit: 100 }), getReviewStats()]);
      setItems(pending.items);
      setStats(st);
      setSelected(new Set());
    } catch (err) {
      console.error("[review] Failed to load:", err);
      reportClientError({ scope: "DiscoveryView:load", error: err });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is stable (useCallback with [])
  useEffect(() => {
    void refresh();
  }, []);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const handleApproveSelected = async () => {
    if (selected.size === 0) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await approveProjects([...selected]);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Approve failed";
      setActionError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveAll = async () => {
    if (
      !window.confirm(
        `Score all ${items.length} pending projects? This will enqueue audits for all of them.`
      )
    )
      return;
    setActionLoading(true);
    setActionError(null);
    try {
      await approveAllProjects();
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Approve all failed";
      setActionError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = () => {
    if (selected.size === 0) return;
    // Snapshot the selected projects into editable rows. Default reason is
    // has_token (common case); each row is independently editable so
    // mixed-reason batches are the first-class flow.
    const rows: RejectRow[] = items
      .filter((i) => selected.has(i.id))
      .map((i) => ({
        projectId: i.id,
        name: i.name ?? null,
        handle: i.twitter_handle ?? "",
        reason: "has_token",
        note: "",
      }));
    setRejectRows(rows);
    setActionError(null);
    setRejectModalOpen(true);
  };

  const updateRejectRow = (projectId: string, patch: Partial<RejectRow>) => {
    setRejectRows((prev) => prev.map((r) => (r.projectId === projectId ? { ...r, ...patch } : r)));
  };

  const applyReasonToAll = (reason: string) => {
    setRejectRows((prev) => prev.map((r) => ({ ...r, reason })));
  };

  // Submit blocked if any "other" row has no note (same rule as the
  // pre-v2-batch-UI modal — "other" needs context to be useful later).
  const submitDisabled =
    actionLoading ||
    rejectRows.length === 0 ||
    rejectRows.some((r) => r.reason === "other" && !r.note.trim());

  const handleRejectSelected = async () => {
    if (rejectRows.length === 0) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await rejectProjects({
        rejections: rejectRows.map((r) => ({
          projectId: r.projectId,
          reason: r.reason,
          note: r.note.trim() || undefined,
        })),
      });
      setRejectModalOpen(false);
      setRejectRows([]);
      await refresh();
      // Warn if any submitted rows were no longer pending by the time
      // the backend got to them (approved/rejected/deleted in another
      // tab since this modal was opened). Silent partial success
      // otherwise leaves the operator thinking every row landed.
      if (result.stale > 0) {
        setActionError(
          `${result.rejected}/${result.rejected + result.stale} rejected — ${result.stale} were already handled elsewhere.`
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Reject failed";
      setActionError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="discovery-panel" style={{ padding: "1.5rem" }}>
        <div className="spinner" style={{ margin: "0 auto" }} />
      </div>
    );
  }

  return (
    <div className="review-queue">
      {/* Stats bar */}
      {stats && (
        <div className="review-stats">
          <span className="review-stats__item">
            <span className="review-stats__count">{stats.pending}</span> pending
          </span>
          <span className="review-stats__item">
            <span className="review-stats__count review-stats__count--approved">
              {stats.approved}
            </span>{" "}
            approved
          </span>
          <span className="review-stats__item">
            <span className="review-stats__count review-stats__count--rejected">
              {stats.rejected}
            </span>{" "}
            rejected
          </span>
          {Object.entries(stats.pendingBySource).map(([src, count]) => (
            <span
              key={src}
              className={`discovery-feed-source discovery-feed-source--${src}`}
              style={{ fontSize: "0.6rem" }}
            >
              {SOURCE_LABELS[src] ?? src}: {count}
            </span>
          ))}
        </div>
      )}

      {/* Bulk actions */}
      <div className="review-actions">
        <label className="review-select-all">
          <input
            type="checkbox"
            checked={items.length > 0 && selected.size === items.length}
            onChange={toggleSelectAll}
          />
          <span>{selected.size > 0 ? `${selected.size} selected` : "Select all"}</span>
        </label>
        <button
          type="button"
          className="btn btn-small btn-primary"
          disabled={selected.size === 0 || actionLoading}
          onClick={handleApproveSelected}
        >
          Score Selected
        </button>
        <button
          type="button"
          className="btn btn-small btn-danger"
          disabled={selected.size === 0 || actionLoading}
          onClick={openRejectModal}
        >
          Reject Selected
        </button>
        <button
          type="button"
          className="btn btn-small btn-secondary"
          disabled={items.length === 0 || actionLoading}
          onClick={handleApproveAll}
        >
          Score All Pending
        </button>
        <button
          type="button"
          className="btn btn-small btn-secondary"
          onClick={() => void refresh()}
        >
          Refresh
        </button>
      </div>

      {/* Error banner */}
      {actionError && (
        <button type="button" className="review-error" onClick={() => setActionError(null)}>
          {actionError}
        </button>
      )}

      {/* Project list */}
      {items.length === 0 ? (
        <div className="empty-movers">No pending projects to review</div>
      ) : (
        <div className="review-list">
          {items.map((item) => {
            const displayName = item.name?.trim() || "Unnamed";
            const isPlaceholderName =
              (item.name ?? "").trim().toLowerCase() === (item.twitter_handle ?? "").toLowerCase();
            const sourceLabel =
              SOURCE_LABELS[item.discovery_source ?? ""] ?? item.discovery_source ?? "?";
            const context = buildDiscoveryContext(item);
            const tags = pickRelevantTags(item);
            return (
              <div
                key={item.id}
                className={`review-card ${selected.has(item.id) ? "review-card--selected" : ""}`}
              >
                <input
                  type="checkbox"
                  className="review-card__checkbox"
                  checked={selected.has(item.id)}
                  onChange={() => toggleSelect(item.id)}
                />
                <div className="review-card__body">
                  <div className="review-card__header">
                    <button
                      type="button"
                      className="review-card__identity"
                      onClick={() => onOpenProject(item.id)}
                    >
                      <span className="review-card__name">
                        {isPlaceholderName ? `@${item.twitter_handle}` : displayName}
                      </span>
                      {!isPlaceholderName && (
                        <span className="review-card__handle">@{item.twitter_handle}</span>
                      )}
                    </button>
                    <a
                      href={`https://twitter.com/${item.twitter_handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="review-card__twitter"
                      title={`Open @${item.twitter_handle} on Twitter/X`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Simple Twitter glyph; avoids a new icon dep */}
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="currentColor"
                      >
                        <path d="M18.244 2H21l-6.52 7.47L22 22h-6.75l-4.7-6.11L5 22H2.243l7-8.02L2 2h6.91l4.24 5.58L18.244 2Zm-2.37 18h1.87L7.25 4H5.27l10.6 16Z" />
                      </svg>
                    </a>
                    {item.website_url && (
                      <a
                        href={item.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="review-card__website"
                        title={item.website_url}
                        onClick={(e) => e.stopPropagation()}
                      >
                        🌐
                      </a>
                    )}
                    <span
                      className={`discovery-feed-source discovery-feed-source--${item.discovery_source}`}
                    >
                      {sourceLabel}
                    </span>
                    <span className="review-card__time">{formatRelative(item.created_at)}</span>
                  </div>
                  {context && <div className="review-card__context">{context}</div>}
                  <div className="review-card__meta">
                    {item.funding_raised !== null && item.funding_raised > 0 && (
                      <span className="review-card__funding">
                        ${(item.funding_raised / 1e6).toFixed(1)}M raised
                      </span>
                    )}
                    {item.lead_investors && item.lead_investors.length > 0 && (
                      <span
                        className="review-card__investors"
                        title={item.lead_investors.join(", ")}
                      >
                        Led by {item.lead_investors.slice(0, 2).join(", ")}
                        {item.lead_investors.length > 2
                          ? ` +${item.lead_investors.length - 2}`
                          : ""}
                      </span>
                    )}
                    {tags.length > 0 && (
                      <span className="review-card__tags">
                        {tags.slice(0, 4).map((t) => (
                          <span key={t} className="review-card__tag">
                            {t}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <div className="review-card__description">
                      {item.description.length > 240
                        ? `${item.description.slice(0, 240).trim()}…`
                        : item.description}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reject modal */}
      {rejectModalOpen && (
        // biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop dismiss
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setRejectModalOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setRejectModalOpen(false)}
        >
          <div
            className="review-modal"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h3>
              Reject {rejectRows.length} project{rejectRows.length !== 1 ? "s" : ""}
            </h3>
            <p className="review-modal__subtitle">
              Each project has its own reason — set them individually, or pick one from "Apply to
              all" below to bulk-set. Empty "other" rows must include a note.
            </p>
            {rejectRows.length > 1 && (
              <label className="review-modal__field review-modal__field--inline">
                <span>Apply to all</span>
                <select
                  value={applyAllReason}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next) {
                      applyReasonToAll(next);
                      setApplyAllReason("");
                    }
                  }}
                >
                  <option value="">— pick a reason to set all rows —</option>
                  {REJECTION_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="review-modal__rows">
              {rejectRows.map((row) => {
                const effect = REJECTION_REASONS.find((r) => r.value === row.reason)?.effect;
                const isOtherWithoutNote = row.reason === "other" && !row.note.trim();
                // Match the placeholder-name check the list card uses
                // (line 575) so Moni/Exa handle=name records render
                // consistently here and there.
                const isPlaceholder =
                  (row.name ?? "").trim().toLowerCase() === row.handle.toLowerCase();
                const displayName = row.name && !isPlaceholder ? row.name : null;
                return (
                  <div
                    key={row.projectId}
                    className={`review-modal__row${isOtherWithoutNote ? " review-modal__row--invalid" : ""}`}
                  >
                    <div className="review-modal__row-head">
                      <span className="review-modal__row-name">
                        {displayName ?? `@${row.handle}`}
                      </span>
                      {displayName && (
                        <span className="review-modal__row-handle">@{row.handle}</span>
                      )}
                    </div>
                    <div className="review-modal__row-controls">
                      <select
                        aria-label={`Reason for ${row.handle}`}
                        value={row.reason}
                        onChange={(e) => updateRejectRow(row.projectId, { reason: e.target.value })}
                      >
                        {REJECTION_REASONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        aria-label={`Note for ${row.handle}`}
                        value={row.note}
                        onChange={(e) => updateRejectRow(row.projectId, { note: e.target.value })}
                        placeholder={
                          row.reason === "other" ? "Note (required)..." : "Note (optional)..."
                        }
                        maxLength={500}
                      />
                    </div>
                    {effect && <div className="review-modal__row-effect">{effect}</div>}
                  </div>
                );
              })}
            </div>
            <div className="review-modal__actions">
              <button
                type="button"
                className="btn btn-small btn-secondary"
                onClick={() => setRejectModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-small btn-danger"
                disabled={submitDisabled}
                onClick={handleRejectSelected}
              >
                {actionLoading
                  ? "Rejecting..."
                  : `Reject ${rejectRows.length} project${rejectRows.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DiscoveryView({ onOpenProjectDetail }: DiscoveryViewProps) {
  async function handleOpenProject(projectId: string) {
    const project = await getProjectById(projectId);
    if (project) onOpenProjectDetail(project);
  }
  const discoveryQuery = useDiscoveryStatusQuery(true);
  const discoveryData = discoveryQuery.data ?? null;
  const discoveryLoading = discoveryQuery.isLoading;

  if (discoveryLoading) {
    return (
      <div className="discovery-view">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading discovery status</span>
        </div>
      </div>
    );
  }

  if (!discoveryData) {
    return (
      <div className="discovery-view">
        <div className="empty-state">
          <h3>No discovery status available</h3>
          <p>Start the worker to begin scheduled discovery runs</p>
        </div>
      </div>
    );
  }

  const {
    currentJob,
    auditPolicy,
    rateLimits,
    lastRun,
    sources,
    recentDiscoveries,
    nextScheduledRun,
    auditQueueDepth,
  } = discoveryData;
  const progressPercent =
    currentJob.progress.totalCandidates > 0
      ? Math.min(
          100,
          Math.round(
            (currentJob.progress.processedCandidates / currentJob.progress.totalCandidates) * 100
          )
        )
      : 0;

  const queueStatus = (discoveryData as unknown as Record<string, unknown>).queueStatus as
    | {
        paused: boolean;
        queues: Record<string, boolean>;
        globalTrip: { tripped: boolean; trippedBy: string | null; lastError: string | null };
      }
    | undefined;

  return (
    <div className="discovery-view">
      {queueStatus?.paused && (
        <div className="queue-paused-banner">
          <div className="queue-paused-banner__title">SCORING PAUSED</div>
          <div className="queue-paused-banner__reason">
            {queueStatus.globalTrip.tripped
              ? `Provider "${queueStatus.globalTrip.trippedBy}" failed repeatedly`
              : "Queues manually paused"}
          </div>
          {queueStatus.globalTrip.lastError && (
            <div className="queue-paused-banner__error">{queueStatus.globalTrip.lastError}</div>
          )}
          <div className="queue-paused-banner__queues">
            {Object.entries(queueStatus.queues)
              .filter(([, v]) => v)
              .map(([k]) => (
                <span key={k} className="queue-paused-banner__queue-chip">
                  {k}
                </span>
              ))}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="discovery-view__section-header">
        <span className="section-title">Discovery Pipeline</span>
        <div className="discovery-view__actions">
          <span
            className={`discovery-status-pill ${currentJob.status === "running" ? "discovery-status-pill--running" : "discovery-status-pill--idle"}`}
          >
            {currentJob.status === "running" ? "RUNNING" : "IDLE"}
          </span>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={() => void discoveryQuery.refetch()}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Top panels: Current Run + Rate Limits + Sources */}
      <div className="discovery-panels discovery-panels--3col">
        {/* Current Run */}
        <div className="discovery-panel">
          <div className="discovery-panel__title">Current Run</div>
          <div className="discovery-progress-track">
            <div className="discovery-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="discovery-kv-grid">
            <div className="discovery-kv">
              <span className="discovery-kv__val">
                {currentJob.progress.processedCandidates}/{currentJob.progress.totalCandidates}
              </span>
              <span className="discovery-kv__key">Processed</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val discovery-kv__val--positive">
                {currentJob.progress.discovered}
              </span>
              <span className="discovery-kv__key">Discovered</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val">{currentJob.progress.enqueued}</span>
              <span className="discovery-kv__key">Enqueued</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val">{currentJob.progress.auditSkippedByPolicy}</span>
              <span className="discovery-kv__key">Deferred</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val">
                {formatEta(currentJob.estimatedMinutesRemaining)}
              </span>
              <span className="discovery-kv__key">ETA</span>
            </div>
          </div>
          <div className="discovery-meta-row">
            <span>Step: {currentJob.progress.step}</span>
            <span>Policy: {formatAuditPolicy(currentJob.progress.auditEnqueuePolicy)}</span>
          </div>
          <div className="discovery-meta-row">
            <span>Next: {formatDateTime(nextScheduledRun)}</span>
            <span>
              Queue: {auditQueueDepth.waiting}w / {auditQueueDepth.active}a
            </span>
          </div>
          <div className="discovery-meta-row">
            <span>
              Default: {formatAuditPolicy(auditPolicy.configured)} ({auditPolicy.configuredFrom})
            </span>
          </div>
        </div>

        {/* Rate Limits */}
        <div className="discovery-panel">
          <div className="discovery-panel__title">Rate Limits</div>
          <div className="discovery-kv-grid">
            <div className="discovery-kv">
              <span className="discovery-kv__val">{rateLimits.coingecko.tier.toUpperCase()}</span>
              <span className="discovery-kv__key">CoinGecko Tier</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val">{rateLimits.coingecko.theoreticalRpm}</span>
              <span className="discovery-kv__key">Theo RPM</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val">{rateLimits.coingecko.observedRpm ?? "—"}</span>
              <span className="discovery-kv__key">Observed RPM</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val">
                {rateLimits.cmc.tier === "disabled" ? "OFF" : rateLimits.cmc.tier.toUpperCase()}
              </span>
              <span className="discovery-kv__key">CMC Tier</span>
            </div>
            <div className="discovery-kv">
              <span className="discovery-kv__val">
                {rateLimits.cmc.usedToday}/{rateLimits.cmc.dailyBudget}
              </span>
              <span className="discovery-kv__key">CMC Today</span>
            </div>
          </div>
          {rateLimits.coingecko.tier === "free" && (
            <p className="discovery-callout">
              Add COINGECKO_DEMO_KEY to move from ~10 RPM to ~30 RPM.
            </p>
          )}
        </div>

        {/* Sources */}
        <div className="discovery-panel">
          <div className="discovery-panel__title">Discovery Sources</div>
          <div className="discovery-sources-list">
            {Object.entries(sources).map(([id, status]) => (
              <SourceRow key={id} id={id} status={status} />
            ))}
          </div>
        </div>
      </div>

      {/* Last Run Summary */}
      {lastRun && (
        <section className="discovery-view__section">
          <LastRunSummary run={lastRun} />
        </section>
      )}

      {/* Review Queue */}
      <section className="discovery-view__section">
        <div className="discovery-view__section-header">
          <span className="section-title">Review Queue</span>
        </div>
        <ReviewQueue onOpenProject={(id) => void handleOpenProject(id)} />
      </section>

      {/* Feed */}
      <section className="discovery-view__section">
        <div className="discovery-view__section-header">
          <span className="section-title">Incoming Projects Feed</span>
          <span className="section-count">Latest 20</span>
        </div>
        <div className="discovery-feed-panel">
          {recentDiscoveries.length === 0 ? (
            <div className="empty-movers">No recent discoveries yet</div>
          ) : (
            recentDiscoveries.map((item) => {
              const displayName = item.name?.trim() || "Unnamed project";
              const sourceLabel = SOURCE_LABELS[item.source] ?? item.source;
              return (
                <div key={item.projectId} className="discovery-feed-row">
                  <button
                    type="button"
                    className="discovery-feed-identity discovery-feed-identity--clickable"
                    onClick={() => handleOpenProject(item.projectId)}
                  >
                    <span className="discovery-feed-name" title={displayName}>
                      {displayName}
                    </span>
                    <span className="discovery-feed-handle">@{item.twitterHandle}</span>
                  </button>
                  <span className={`discovery-feed-source discovery-feed-source--${item.source}`}>
                    {sourceLabel}
                  </span>
                  <span className="discovery-feed-time">{formatRelative(item.discoveredAt)}</span>
                  <span
                    className={`discovery-feed-status discovery-feed-status--${item.auditStatus}`}
                  >
                    {formatQueueStatus(item.auditStatus)}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
